import React, { useState, useEffect, useRef } from 'react';
import { getProduits, createProduit, updateProduit, getCategories, createCategorie, updateCategorie, deleteCategorie } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
    FiPlus, FiEdit2, FiSearch, FiTag, FiGrid, FiList,
    FiFilter, FiImage, FiPackage, FiX, FiChevronDown,
    FiDollarSign, FiCamera, FiTrash2, FiRefreshCw
} from 'react-icons/fi';

const API_BASE = 'http://localhost:5055';
const TYPES_ACTIVITE = ['vente', 'commande', 'visite', 'installation', 'sensibilisation', 'inscription', 'recrutement'];

const Produits: React.FC = () => {
    const { isAdmin, isManager } = useAuth();
    const [produits, setProduits] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [search, setSearch] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterActif, setFilterActif] = useState('');
    const [filterPrixMin, setFilterPrixMin] = useState('');
    const [filterPrixMax, setFilterPrixMax] = useState('');
    const [sortBy, setSortBy] = useState('nom');
    const [sortOrder, setSortOrder] = useState('asc');
    const [showFilters, setShowFilters] = useState(false);

    // View mode
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [showCatModal, setShowCatModal] = useState(false);
    const [editProduit, setEditProduit] = useState<any>(null);
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [form, setForm] = useState<any>({
        nom: '', description: '', reference: '', categorie_id: '', prix: '',
        unite: '', objectif_defaut: 0, points_gamification: 0
    });
    const [catForm, setCatForm] = useState({ nom: '', type: 'vente', description: '', couleur: '#2196F3' });
    const [editCat, setEditCat] = useState<any>(null);
    const [catSaving, setCatSaving] = useState(false);

    // Stats
    const [stats, setStats] = useState<any>(null);

    const loadData = () => {
        setLoading(true);
        const params: any = {};
        if (search) params.search = search;
        if (filterCategorie) params.categorie_id = filterCategorie;
        if (filterType) params.type = filterType;
        if (filterActif !== '') params.actif = filterActif;
        if (filterPrixMin) params.prix_min = filterPrixMin;
        if (filterPrixMax) params.prix_max = filterPrixMax;
        if (sortBy) params.sort = sortBy;
        if (sortOrder) params.order = sortOrder;

        getProduits(params).then(res => setProduits(res.data)).finally(() => setLoading(false));
        getCategories().then(res => setCategories(res.data));
        // Stats
        const token = localStorage.getItem('sp_token');
        fetch(`${API_BASE}/api/produits/stats`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(s => setStats(s)).catch(() => {});
    };

    useEffect(() => { loadData(); }, [search, filterCategorie, filterType, filterActif, filterPrixMin, filterPrixMax, sortBy, sortOrder]);

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('sp_token');
            const formData = new FormData();
            Object.entries(form).forEach(([key, value]) => {
                if (value !== undefined && value !== null) formData.append(key, String(value));
            });
            if (photoFile) formData.append('photo', photoFile);

            const url = editProduit
                ? `${API_BASE}/api/produits/${editProduit.id}`
                : `${API_BASE}/api/produits`;

            await fetch(url, {
                method: editProduit ? 'PUT' : 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            setShowModal(false);
            handleRemovePhoto();
            loadData();
        } catch { alert('Erreur lors de la sauvegarde'); }
    };

    const handleCatSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setCatSaving(true);
        try {
            if (editCat) {
                await updateCategorie(editCat.id, catForm);
            } else {
                await createCategorie(catForm);
            }
            setCatForm({ nom: '', type: 'vente', description: '', couleur: '#2196F3' });
            setEditCat(null);
            loadData();
        } catch (err: any) {
            alert(err?.response?.data?.error || 'Erreur');
        }
        setCatSaving(false);
    };

    const handleCatEdit = (cat: any) => {
        setEditCat(cat);
        setCatForm({ nom: cat.nom, type: cat.type, description: cat.description || '', couleur: cat.couleur || '#2196F3' });
    };

    const handleCatDelete = async (cat: any) => {
        if (!window.confirm(`Supprimer la categorie "${cat.nom}" ?`)) return;
        try {
            await deleteCategorie(cat.id);
            loadData();
        } catch (err: any) {
            alert(err?.response?.data?.error || 'Erreur lors de la suppression');
        }
    };

    const handleCatCancel = () => {
        setEditCat(null);
        setCatForm({ nom: '', type: 'vente', description: '', couleur: '#2196F3' });
    };

    const openCreate = () => {
        setEditProduit(null);
        setForm({ nom: '', description: '', reference: '', categorie_id: categories[0]?.id || '', prix: '', unite: '', objectif_defaut: 0, points_gamification: 0 });
        handleRemovePhoto();
        setShowModal(true);
    };

    const openEdit = (p: any) => {
        setEditProduit(p);
        setForm({
            nom: p.nom, description: p.description || '', reference: p.reference || '',
            categorie_id: p.categorie_id, prix: p.prix || '', unite: p.unite || '',
            objectif_defaut: p.objectif_defaut, points_gamification: p.points_gamification,
            actif: p.actif
        });
        setPhotoPreview(p.photo_url ? `${API_BASE}${p.photo_url}` : '');
        setPhotoFile(null);
        setShowModal(true);
    };

    const resetFilters = () => {
        setSearch(''); setFilterCategorie(''); setFilterType('');
        setFilterActif(''); setFilterPrixMin(''); setFilterPrixMax('');
        setSortBy('nom'); setSortOrder('asc');
    };

    const activeFilterCount = [filterCategorie, filterType, filterActif, filterPrixMin, filterPrixMax].filter(Boolean).length;

    return (
        <div className="page-produits-v2">
            {/* Header */}
            <div className="page-header">
                <h2><FiPackage /> Produits & Services</h2>
                <div className="header-actions">
                    {isAdmin() && (
                        <>
                            <button className="btn btn-secondary" onClick={() => setShowCatModal(true)}>
                                <FiTag /> Categorie
                            </button>
                            <button className="btn btn-primary" onClick={openCreate}>
                                <FiPlus /> Nouveau produit
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="kpi-grid" style={{ marginBottom: 16, gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    <div className="kpi-card" style={{ borderLeftColor: '#16A34A' }}>
                        <div className="kpi-content">
                            <span className="kpi-value">{stats.total || 0}</span>
                            <span className="kpi-label">Total produits</span>
                        </div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#22C55E' }}>
                        <div className="kpi-content">
                            <span className="kpi-value">{stats.actifs || 0}</span>
                            <span className="kpi-label">Actifs</span>
                        </div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#F59E0B' }}>
                        <div className="kpi-content">
                            <span className="kpi-value">{stats.inactifs || 0}</span>
                            <span className="kpi-label">Inactifs</span>
                        </div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#8B5CF6' }}>
                        <div className="kpi-content">
                            <span className="kpi-value">{stats.prix_moyen ? Math.round(stats.prix_moyen).toLocaleString() : 0} F</span>
                            <span className="kpi-label">Prix moyen</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Search & Filters Bar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                <div className="search-box" style={{ flex: 1, minWidth: 200 }}>
                    <FiSearch />
                    <input placeholder="Rechercher par nom, description, reference..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select value={filterCategorie} onChange={e => setFilterCategorie(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
                    <option value="">Toutes categories</option>
                    {categories.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.nom} ({c.nb_produits || 0})</option>
                    ))}
                </select>
                <select value={filterType} onChange={e => setFilterType(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
                    <option value="">Tous les types</option>
                    {TYPES_ACTIVITE.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button
                    className={`btn btn-outline ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                    style={{ position: 'relative' }}
                >
                    <FiFilter /> Filtres
                    {activeFilterCount > 0 && (
                        <span style={{
                            position: 'absolute', top: -6, right: -6,
                            background: '#EF4444', color: '#fff', borderRadius: '50%',
                            width: 18, height: 18, fontSize: 10, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontWeight: 700
                        }}>
                            {activeFilterCount}
                        </span>
                    )}
                </button>
                <div style={{ display: 'flex', gap: 2, background: '#f3f4f6', borderRadius: 8, padding: 2 }}>
                    <button onClick={() => setViewMode('grid')}
                        style={{ padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: viewMode === 'grid' ? '#fff' : 'transparent', boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                        <FiGrid size={16} />
                    </button>
                    <button onClick={() => setViewMode('list')}
                        style={{ padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: viewMode === 'list' ? '#fff' : 'transparent', boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                        <FiList size={16} />
                    </button>
                </div>
            </div>

            {/* Extended filters panel */}
            {showFilters && (
                <div style={{
                    display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap',
                    padding: 16, background: '#f9fafb', borderRadius: 10, marginBottom: 16,
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>Statut</label>
                        <select value={filterActif} onChange={e => setFilterActif(e.target.value)}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                            <option value="">Tous</option>
                            <option value="1">Actifs</option>
                            <option value="0">Inactifs</option>
                        </select>
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>Prix min</label>
                        <input type="number" value={filterPrixMin} onChange={e => setFilterPrixMin(e.target.value)}
                            placeholder="0" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, width: 100 }} />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>Prix max</label>
                        <input type="number" value={filterPrixMax} onChange={e => setFilterPrixMax(e.target.value)}
                            placeholder="999999" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, width: 100 }} />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>Trier par</label>
                        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                            <option value="nom">Nom</option>
                            <option value="prix">Prix</option>
                            <option value="recent">Plus recent</option>
                        </select>
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>Ordre</label>
                        <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                            <option value="asc">Croissant</option>
                            <option value="desc">Decroissant</option>
                        </select>
                    </div>
                    <button className="btn btn-secondary" onClick={resetFilters} style={{ marginLeft: 'auto' }}>
                        <FiRefreshCw size={13} /> Reinitialiser
                    </button>
                </div>
            )}

            {/* Results count */}
            <div style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>
                {produits.length} produit{produits.length !== 1 ? 's' : ''} trouve{produits.length !== 1 ? 's' : ''}
            </div>

            {/* Loading */}
            {loading ? (
                <div className="loading">Chargement...</div>
            ) : produits.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                    <FiPackage size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                    <p>Aucun produit trouve</p>
                    <p style={{ fontSize: 13 }}>Essayez de modifier vos filtres</p>
                </div>
            ) : viewMode === 'grid' ? (
                /* GRID VIEW */
                <div className="cards-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                    {produits.map(p => (
                        <div key={p.id} className="product-card-v2" style={{
                            background: '#fff', borderRadius: 12, overflow: 'hidden',
                            border: '1px solid #e5e7eb', transition: 'all 0.2s',
                            display: 'flex', flexDirection: 'column'
                        }}>
                            {/* Photo */}
                            <div style={{
                                width: '100%', height: 160, background: '#f3f4f6',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                position: 'relative', overflow: 'hidden'
                            }}>
                                {p.photo_url ? (
                                    <img src={`${API_BASE}${p.photo_url}`} alt={p.nom}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <FiImage size={36} style={{ color: '#d1d5db' }} />
                                )}
                                {/* Status badge */}
                                <span style={{
                                    position: 'absolute', top: 8, right: 8,
                                    background: p.actif ? '#dcfce7' : '#fee2e2',
                                    color: p.actif ? '#16A34A' : '#EF4444',
                                    padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700
                                }}>
                                    {p.actif ? 'Actif' : 'Inactif'}
                                </span>
                                {/* Category badge */}
                                <span style={{
                                    position: 'absolute', top: 8, left: 8,
                                    background: p.categorie_couleur || '#16A34A',
                                    color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600
                                }}>
                                    {p.categorie_type}
                                </span>
                            </div>

                            {/* Content */}
                            <div style={{ padding: '12px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#111' }}>{p.nom}</h4>
                                    {isAdmin() && (
                                        <button className="btn-icon" onClick={() => openEdit(p)} style={{ flexShrink: 0 }}>
                                            <FiEdit2 size={14} />
                                        </button>
                                    )}
                                </div>
                                {p.reference && <span style={{ fontSize: 11, color: '#999', fontFamily: 'monospace' }}>Ref: {p.reference}</span>}
                                <p style={{ fontSize: 12, color: '#666', margin: '6px 0', flex: 1, lineHeight: 1.4 }}>
                                    {p.description ? (p.description.length > 80 ? p.description.substring(0, 80) + '...' : p.description) : 'Pas de description'}
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f3f4f6', paddingTop: 8, marginTop: 4 }}>
                                    <span style={{ fontSize: 16, fontWeight: 700, color: '#16A34A' }}>
                                        {p.prix > 0 ? `${Number(p.prix).toLocaleString()} F` : 'N/A'}
                                    </span>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {p.unite && <span style={{ fontSize: 11, background: '#f3f4f6', padding: '2px 8px', borderRadius: 8, color: '#666' }}>/{p.unite}</span>}
                                        <span style={{ fontSize: 11, background: '#f3f4f6', padding: '2px 8px', borderRadius: 8, color: '#666' }}>{p.categorie_nom}</span>
                                    </div>
                                </div>
                                {p.points_gamification > 0 && (
                                    <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4 }}>
                                        {p.points_gamification} pts gamification
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                /* LIST VIEW */
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th style={{ width: 56 }}>Photo</th>
                                <th>Nom</th>
                                <th>Reference</th>
                                <th>Categorie</th>
                                <th>Type</th>
                                <th>Prix</th>
                                <th>Unite</th>
                                <th>Points</th>
                                <th>Statut</th>
                                {isAdmin() && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {produits.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <div style={{
                                            width: 40, height: 40, borderRadius: 8, overflow: 'hidden',
                                            background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            {p.photo_url ? (
                                                <img src={`${API_BASE}${p.photo_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <FiImage size={16} style={{ color: '#d1d5db' }} />
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <strong>{p.nom}</strong>
                                        {p.description && <div style={{ fontSize: 11, color: '#999', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description}</div>}
                                    </td>
                                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.reference || '--'}</td>
                                    <td><span style={{ background: `${p.categorie_couleur}20`, color: p.categorie_couleur, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{p.categorie_nom}</span></td>
                                    <td><span className="badge" style={{ background: p.categorie_couleur, fontSize: 10 }}>{p.categorie_type}</span></td>
                                    <td style={{ fontWeight: 600, color: '#16A34A' }}>{p.prix > 0 ? `${Number(p.prix).toLocaleString()} F` : '--'}</td>
                                    <td>{p.unite || '--'}</td>
                                    <td>{p.points_gamification > 0 ? <span style={{ color: '#F59E0B' }}>{p.points_gamification} pts</span> : '--'}</td>
                                    <td><span className={`status ${p.actif ? 'active' : 'inactive'}`}>{p.actif ? 'Actif' : 'Inactif'}</span></td>
                                    {isAdmin() && (
                                        <td>
                                            <button className="btn-icon" onClick={() => openEdit(p)}><FiEdit2 size={14} /></button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
                        <h3>{editProduit ? 'Modifier' : 'Nouveau'} Produit</h3>
                        <form onSubmit={handleSubmit}>
                            {/* Photo upload */}
                            <div className="form-group">
                                <label><FiCamera size={13} /> Photo du produit</label>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <div style={{
                                        width: 100, height: 100, borderRadius: 10, overflow: 'hidden',
                                        background: '#f3f4f6', border: '2px dashed #d1d5db',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', position: 'relative', flexShrink: 0
                                    }} onClick={() => fileInputRef.current?.click()}>
                                        {photoPreview ? (
                                            <>
                                                <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleRemovePhoto(); }}
                                                    style={{
                                                        position: 'absolute', top: 2, right: 2,
                                                        background: '#EF4444', color: '#fff', border: 'none',
                                                        borderRadius: '50%', width: 20, height: 20,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        cursor: 'pointer', fontSize: 10
                                                    }}>
                                                    <FiX size={12} />
                                                </button>
                                            </>
                                        ) : (
                                            <div style={{ textAlign: 'center', color: '#999' }}>
                                                <FiCamera size={20} />
                                                <div style={{ fontSize: 10, marginTop: 4 }}>Ajouter</div>
                                            </div>
                                        )}
                                    </div>
                                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange}
                                        style={{ display: 'none' }} />
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        <p style={{ margin: 0 }}>Cliquez pour ajouter une photo</p>
                                        <p style={{ margin: '4px 0 0' }}>JPG, PNG, WebP - Max 5Mo</p>
                                    </div>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group" style={{ flex: 2 }}>
                                    <label>Nom *</label>
                                    <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required placeholder="Nom du produit" />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Reference</label>
                                    <input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="REF-001" />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description du produit..." />
                            </div>

                            <div className="form-group">
                                <label>Categorie *</label>
                                <select value={form.categorie_id} onChange={e => setForm({ ...form, categorie_id: Number(e.target.value) })} required>
                                    <option value="">Selectionner une categorie</option>
                                    {categories.map((c: any) => <option key={c.id} value={c.id}>{c.nom} ({c.type})</option>)}
                                </select>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label><FiDollarSign size={13} /> Prix (F CFA)</label>
                                    <input type="number" step="any" value={form.prix} onChange={e => setForm({ ...form, prix: e.target.value })} placeholder="0" />
                                </div>
                                <div className="form-group">
                                    <label>Unite</label>
                                    <input value={form.unite} onChange={e => setForm({ ...form, unite: e.target.value })} placeholder="piece, kg, sac..." />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Objectif par defaut</label>
                                    <input type="number" value={form.objectif_defaut} onChange={e => setForm({ ...form, objectif_defaut: Number(e.target.value) })} />
                                </div>
                                <div className="form-group">
                                    <label>Points gamification</label>
                                    <input type="number" value={form.points_gamification} onChange={e => setForm({ ...form, points_gamification: Number(e.target.value) })} />
                                </div>
                            </div>

                            {editProduit && (
                                <div className="form-group">
                                    <label>Statut</label>
                                    <select value={form.actif} onChange={e => setForm({ ...form, actif: Number(e.target.value) })}>
                                        <option value={1}>Actif</option>
                                        <option value={0}>Inactif</option>
                                    </select>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">{editProduit ? 'Mettre a jour' : 'Creer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Category Management Modal */}
            {showCatModal && (
                <div className="modal-overlay" onClick={() => { setShowCatModal(false); handleCatCancel(); }}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ margin: 0 }}><FiTag /> Gestion des Categories</h3>
                            <button className="btn-icon" onClick={() => { setShowCatModal(false); handleCatCancel(); }}><FiX size={18} /></button>
                        </div>

                        {/* Category form */}
                        <div style={{ background: '#f9fafb', borderRadius: 10, padding: 16, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                            <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#374151' }}>
                                {editCat ? `Modifier: ${editCat.nom}` : 'Nouvelle categorie'}
                            </h4>
                            <form onSubmit={handleCatSubmit}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ fontSize: 12 }}>Nom *</label>
                                        <input value={catForm.nom} onChange={e => setCatForm({ ...catForm, nom: e.target.value })} required placeholder="Nom de la categorie" />
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ fontSize: 12 }}>Type d'activite</label>
                                        <select value={catForm.type} onChange={e => setCatForm({ ...catForm, type: e.target.value })}>
                                            {TYPES_ACTIVITE.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ fontSize: 12 }}>Description</label>
                                        <input value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} placeholder="Description optionnelle" />
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ fontSize: 12 }}>Couleur</label>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <input type="color" value={catForm.couleur} onChange={e => setCatForm({ ...catForm, couleur: e.target.value })} style={{ width: 40, height: 36, border: 'none', cursor: 'pointer' }} />
                                            <span style={{ fontSize: 12, color: '#999', fontFamily: 'monospace' }}>{catForm.couleur}</span>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                                    {editCat && (
                                        <button type="button" className="btn btn-secondary" onClick={handleCatCancel}>Annuler</button>
                                    )}
                                    <button type="submit" className="btn btn-primary" disabled={catSaving}>
                                        {catSaving ? 'En cours...' : editCat ? 'Mettre a jour' : <><FiPlus size={13} /> Ajouter</>}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Categories list */}
                        <div>
                            <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#374151' }}>
                                Categories existantes ({categories.length})
                            </h4>
                            {categories.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>
                                    <FiTag size={30} style={{ opacity: 0.3, marginBottom: 8 }} />
                                    <p style={{ fontSize: 13 }}>Aucune categorie creee</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {categories.map((cat: any) => (
                                        <div key={cat.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 12,
                                            padding: '12px 16px', background: editCat?.id === cat.id ? '#eff6ff' : '#fff',
                                            borderRadius: 10, border: editCat?.id === cat.id ? '2px solid #3B82F6' : '1px solid #e5e7eb',
                                            transition: 'all 0.15s'
                                        }}>
                                            {/* Color dot */}
                                            <div style={{
                                                width: 12, height: 12, borderRadius: '50%',
                                                background: cat.couleur || '#999', flexShrink: 0
                                            }} />

                                            {/* Info */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <strong style={{ fontSize: 14 }}>{cat.nom}</strong>
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 600, padding: '2px 8px',
                                                        borderRadius: 10, background: `${cat.couleur || '#999'}18`,
                                                        color: cat.couleur || '#666'
                                                    }}>
                                                        {cat.type}
                                                    </span>
                                                </div>
                                                {cat.description && (
                                                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{cat.description}</div>
                                                )}
                                            </div>

                                            {/* Product count */}
                                            <span style={{
                                                fontSize: 12, color: '#666', background: '#f3f4f6',
                                                padding: '4px 10px', borderRadius: 8, whiteSpace: 'nowrap'
                                            }}>
                                                {cat.nb_produits || 0} produit{(cat.nb_produits || 0) !== 1 ? 's' : ''}
                                            </span>

                                            {/* Actions */}
                                            {isAdmin() && (
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    <button className="btn-icon" onClick={() => handleCatEdit(cat)}
                                                        title="Modifier" style={{ color: '#3B82F6' }}>
                                                        <FiEdit2 size={14} />
                                                    </button>
                                                    <button className="btn-icon" onClick={() => handleCatDelete(cat)}
                                                        title="Supprimer" style={{ color: '#EF4444' }}>
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Produits;
